import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, RotateCcw, RefreshCcw, AlertCircle, Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { errMsg } from "@/lib/errors";

/**
 * 7-day soft-delete trash for bookings.
 *
 * iter186 (02/2026) — Multi-select: l'host può selezionare più elementi
 * (checkbox su ogni riga + "Seleziona tutto") e:
 *   • Elimina definitivamente in blocco
 *   • Ripristina in blocco
 * Le azioni singole restano disponibili sulla riga.
 */
export default function TrashDialog({ open, onOpenChange, onRestored }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [busyBulk, setBusyBulk] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await api.get("/admin/bookings/deleted");
      setItems(r.data?.items || []);
      setSelected(new Set());
    } catch (e) {
      toast.error(errMsg(e, "Errore caricamento cestino"));
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = useMemo(
    () => items.length > 0 && items.every((it) => selected.has(it.id)),
    [items, selected]
  );
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((it) => it.id)));
  };

  const handleRestore = async (it) => {
    try {
      await api.post(`/admin/bookings/${it.id}/restore`);
      toast.success(`✓ ${it.code} ripristinata`);
      load();
      onRestored?.();
    } catch (e) {
      toast.error(errMsg(e, "Errore ripristino"));
    }
  };

  const handlePurge = async (it) => {
    if (!window.confirm(
      `Eliminare DEFINITIVAMENTE ${it.code}?\n\nNon sarà più recuperabile.`
    )) return;
    try {
      await api.delete(`/admin/bookings/deleted/${it.id}/purge`);
      toast.success(`✓ ${it.code} eliminata definitivamente`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore eliminazione"));
    }
  };

  const bulkRestore = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Ripristinare ${selected.size} prenotazione${selected.size > 1 ? "i" : ""}?`)) return;
    setBusyBulk(true);
    let ok = 0, ko = 0;
    for (const id of selected) {
      try {
        await api.post(`/admin/bookings/${id}/restore`);
        ok++;
      } catch {
        ko++;
      }
    }
    setBusyBulk(false);
    if (ok > 0) toast.success(`✓ ${ok} ripristinate${ko > 0 ? ` · ${ko} errori` : ""}`);
    if (ok === 0 && ko > 0) toast.error(`Errore su tutte (${ko})`);
    load();
    onRestored?.();
  };

  const bulkPurge = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(
      `Eliminare DEFINITIVAMENTE ${selected.size} prenotazione${selected.size > 1 ? "i" : ""}?\n\n` +
      "Non saranno più recuperabili."
    )) return;
    setBusyBulk(true);
    let ok = 0, ko = 0;
    for (const id of selected) {
      try {
        await api.delete(`/admin/bookings/deleted/${id}/purge`);
        ok++;
      } catch {
        ko++;
      }
    }
    setBusyBulk(false);
    if (ok > 0) toast.success(`✓ ${ok} eliminate${ko > 0 ? ` · ${ko} errori` : ""}`);
    if (ok === 0 && ko > 0) toast.error(`Errore su tutte (${ko})`);
    load();
  };

  const daysLeft = (deletedAtISO) => {
    if (!deletedAtISO) return null;
    try {
      const deletedAt = new Date(deletedAtISO);
      const purgeAt = new Date(deletedAt.getTime() + 7 * 24 * 60 * 60 * 1000);
      const diffMs = purgeAt.getTime() - Date.now();
      if (diffMs <= 0) return 0;
      return Math.ceil(diffMs / (24 * 60 * 60 * 1000));
    } catch {
      return null;
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return format(parseISO(iso), "dd/MM/yyyy", { locale: itLocale }); }
    catch { return iso; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="trash-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1E3A5F]">
            <Trash2 className="w-5 h-5 text-rose-600" />
            Cestino prenotazioni
          </DialogTitle>
          <DialogDescription>
            Le prenotazioni eliminate restano qui per <strong>7 giorni</strong>, poi vengono cancellate automaticamente.
            Puoi selezionare più righe con le caselle a sinistra per un&apos;azione in blocco.
          </DialogDescription>
        </DialogHeader>

        {/* TOOLBAR */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          {items.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-[#1E3A5F] cursor-pointer select-none">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleAll}
                data-state={allSelected ? "checked" : someSelected ? "indeterminate" : "unchecked"}
                data-testid="trash-select-all"
              />
              <span>{allSelected ? "Deseleziona tutto" : selected.size > 0 ? `${selected.size} selezionate` : "Seleziona tutto"}</span>
            </label>
          )}
          <div className="flex gap-2 ml-auto">
            {selected.size > 0 && (
              <>
                <Button
                  size="sm"
                  onClick={bulkRestore}
                  disabled={busyBulk}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="trash-bulk-restore"
                >
                  {busyBulk ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                  Ripristina {selected.size}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={bulkPurge}
                  disabled={busyBulk}
                  className="text-rose-700 border-rose-300 hover:bg-rose-50"
                  data-testid="trash-bulk-purge"
                >
                  {busyBulk ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                  Elimina {selected.size}
                </Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="trash-refresh">
              <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Aggiorna
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10 text-[#635F59] text-sm">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-[#635F59]">
            <Trash2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm font-medium">Il cestino è vuoto</p>
            <p className="text-xs mt-1">Le prenotazioni eliminate appariranno qui per 7 giorni.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="trash-items">
            {items.map((it) => {
              const left = daysLeft(it.deleted_at);
              const guestName = [it.guest?.first_name, it.guest?.last_name].filter(Boolean).join(" ") || "(senza nome)";
              const isSel = selected.has(it.id);
              return (
                <div
                  key={it.id}
                  className={`border rounded-lg p-3 flex items-start gap-3 transition-colors ${isSel ? "border-[#AC5D45] bg-[#FBF3EF]" : "border-[#E8E2D9] bg-[#FDFBF7]"}`}
                  data-testid={`trash-item-${it.code}`}
                >
                  <div className="pt-0.5">
                    <Checkbox
                      checked={isSel}
                      onCheckedChange={() => toggle(it.id)}
                      data-testid={`trash-select-${it.code}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono font-bold text-[#AC5D45] text-sm">{it.code}</span>
                      <span className="text-sm font-semibold text-[#1E3A5F]">{guestName}</span>
                      {it.status_at_delete && (
                        <span className="text-[10px] uppercase font-bold bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">
                          era: {it.status_at_delete}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#635F59] space-y-0.5">
                      <div>📅 {fmt(it.check_in)} → {fmt(it.check_out)} · {it.room_name ? <span>{typeof it.room_name === "string" ? it.room_name : (it.room_name?.it || "")}</span> : <em>nessuna camera</em>} · €{(it.total || 0).toFixed(2)}</div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>Eliminata il {fmt(it.deleted_at)} da {it.deleted_by || "—"}</span>
                        {left != null && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${left <= 1 ? "bg-rose-100 text-rose-700" : left <= 3 ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                            {left === 0 ? "⏰ ultima ora" : `${left}gg rimanenti`}
                          </span>
                        )}
                      </div>
                      {it.related_counts && Object.values(it.related_counts).some((v) => v > 0) && (
                        <div className="flex items-center gap-1 text-[10px] text-[#635F59] mt-1">
                          <AlertCircle className="w-3 h-3" />
                          Dati collegati: {Object.entries(it.related_counts).filter(([, v]) => v > 0).map(([k, v]) => `${k}=${v}`).join(" · ")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => handleRestore(it)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`trash-restore-${it.code}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Ripristina
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePurge(it)}
                      className="text-rose-700 border-rose-300 hover:bg-rose-50"
                      data-testid={`trash-purge-${it.code}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina ora
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
